import { createElement, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { useSnackbar } from '@/hooks/global-tools';
import {
  CLAIM_NOTE_POPUP_BLOCKED_MESSAGE,
  legacyClaimNoteOz,
  openClaimNoteOzReport,
  type ClaimNoteOzPrint,
} from '@/utils/approval/claimNote';
import { ACTION_CELL_CLASS, CARD_CLASS } from './content';
import { ClaimNoteRoot } from './styles';

export { buildClaimNoteHtml } from './content';

/** 레거시 문서 카드의 버튼 */
const LEGACY_PRINT_CLASS = 'btn-printattachclaim';
const LEGACY_ATTACH_CLASS = 'btn-attachclaim';
const LEGACY_DELETE_CLASS = 'btn-detail-hide';
const LEGACY_REJECT_CLASS = 'btn-detail-reject';

export type ClaimNoteTarget = { itemId: string; label: string };
export type ClaimNoteCardRef = ClaimNoteTarget & { aprvRev: string };

/** 부분반려 버튼을 붙일 카드 수 — V2 카드 + 레거시 부분반려 버튼 */
export const countClaimNoteCards = (content: string | null) =>
  content
    ? (
        content.match(
          new RegExp(`${CARD_CLASS}"|${LEGACY_REJECT_CLASS}`, 'g'),
        ) ?? []
      ).length
    : 0;

/**
 * 저장 카드 HTML 그대로(레거시 문서 포함). Print 는 작성·조회 모두,
 * 삭제는 작성 화면에서만. 업체파일은 첨부 경로가 있을 때만
 */
export const ClaimNoteSnapshot = ({
  content,
  onDelete,
  renderCardAction,
}: {
  content: string;
  onDelete?: (target: ClaimNoteTarget) => void;
  renderCardAction?: (card: ClaimNoteCardRef) => ReactNode;
}) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const { classList } = element;
      // 다운로드 API 대기 — 첨부 경로는 data-attach-path·data-attach_grpcd
      const attachButton = (
        <BoxButton key="attach" type="button" variant="secondary" size="small">
          {t('업체파일')}
        </BoxButton>
      );
      const printButton = (oz: ClaimNoteOzPrint) => (
        <BoxButton
          key="print"
          type="button"
          variant="secondary"
          size="small"
          onClick={() => {
            if (!openClaimNoteOzReport(oz))
              snackbar.showError({
                message: t(CLAIM_NOTE_POPUP_BLOCKED_MESSAGE),
              });
          }}
        >
          Print
        </BoxButton>
      );
      if (classList.contains(ACTION_CELL_CLASS)) {
        const oz: ClaimNoteOzPrint = {
          url: element.getAttribute('data-oz-url'),
          gerpOzrNm: element.getAttribute('data-oz-ozr'),
          gerpOdiNm: element.getAttribute('data-oz-odi'),
          pcount: element.getAttribute('data-oz-pcount'),
          args1: element.getAttribute('data-oz-args1'),
          locale: element.getAttribute('data-oz-locale'),
          claimKind: element.getAttribute('data-oz-kind'),
        };
        const card = element.closest(`.${CARD_CLASS}`);
        const target = {
          itemId: card?.getAttribute('data-item-id') ?? '',
          label: card?.getAttribute('data-label') ?? '',
        };
        return createElement(
          tagName,
          props,
          <ActionButtons key="actions">
            {element.getAttribute('data-attach-path') && attachButton}
            {oz.args1 && printButton(oz)}
            {onDelete && target.itemId && (
              <BoxButton
                type="button"
                variant="secondary"
                size="small"
                onClick={() => onDelete(target)}
              >
                {t('삭제')}
              </BoxButton>
            )}
            {target.itemId &&
              renderCardAction?.({
                ...target,
                aprvRev: card?.getAttribute('data-aprv-rev') ?? '',
              })}
          </ActionButtons>,
        );
      }
      if (classList.contains(LEGACY_PRINT_CLASS)) {
        const claimNo = element.getAttribute('data-claim_no') ?? '';
        const kind = element.getAttribute('data-claim_type') ?? '';
        return claimNo ? printButton(legacyClaimNoteOz(claimNo, kind)) : null;
      }
      if (classList.contains(LEGACY_ATTACH_CLASS))
        return element.getAttribute('data-attach_grpcd') ? attachButton : null;
      if (classList.contains(LEGACY_REJECT_CLASS)) {
        const itemId = element.getAttribute('data-aprv_id') ?? '';
        // 레거시 DEBIT·SCRAP 은 data-claim_no, NOTE 는 data-credit_no
        return itemId
          ? renderCardAction?.({
              itemId,
              label:
                element.getAttribute('data-claim_no') ||
                element.getAttribute('data-credit_no') ||
                '',
              aprvRev: element.getAttribute('data-aprv_rev') ?? '',
            }) ?? null
          : null;
      }
      if (classList.contains(LEGACY_DELETE_CLASS)) return null;
      return undefined;
    },
    [onDelete, renderCardAction, snackbar, t],
  );

  return (
    <ClaimNoteRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </ClaimNoteRoot>
  );
};

const ActionButtons = ({ children }: { children: ReactNode }) => (
  <Box
    component="span"
    sx={{ display: 'inline-flex', gap: '4px', justifyContent: 'flex-end' }}
  >
    {children}
  </Box>
);
